<script lang="ts" setup>
import { POPULAR_SECTION_GAMES_LIMIT } from '~/constants'
import { HOME_SWIPER_OPTIONS, SKELETON_POPULAR_GAME } from '~/resources'

import { PopularSectionTabsEnum, SlidersPerView, KeyEnum } from '~/enums'
import type { GameItem, GamesRequestPayload } from '@kira-dancer/nuxt-common'
import { useForm } from 'vee-validate'
import cacheManager from '#lib/utils/cache-manager'

const $isLighthouseBot = useNuxtApp().$isLighthouseBot as boolean
const { isMobile } = useDevice()
const { requestFetchGames } = useLobbyGames()
const tab = ref(PopularSectionTabsEnum.BigWin)
const isFirstLoad = ref(true)
const params = reactive<GamesRequestPayload>({
  groups: PopularSectionTabsEnum.All,
  limit: POPULAR_SECTION_GAMES_LIMIT,
})
const SECTION_GAMES_LIMIT = 6
const {
  data: lobbyGames,
  refresh,
  status,
} = useAsyncData(KeyEnum.PopularGame, async () => {
  const cachedData = cacheManager.get(KeyEnum.PopularGame)
  if (cachedData && isFirstLoad.value) {
    return cachedData
  }
  try {
    const dataGames = await requestFetchGames(params)
    cacheManager.set(KeyEnum.PopularGame, dataGames)
    return dataGames
  } catch {
    return []
  }
})

const swiperOptions = {
  ...HOME_SWIPER_OPTIONS,
  loop: false,
  navigation: !isMobile
    ? {
        prevEl: '.game-card-swiper-prev',
        nextEl: '.game-card-swiper-next',
      }
    : undefined,
  slidesPerView: isMobile ? SlidersPerView.Mobile : SlidersPerView.Medium,
  spaceBetween: isMobile ? 12 : 24,
}

useForm({
  initialValues: {
    filterName: PopularSectionTabsEnum.All,
  },
})

watch(
  () => tab.value,
  (value) => {
    if (isFirstLoad.value) {
      isFirstLoad.value = false
      return
    }
    onSelectTab(value)
  },
)

const tabItems = [
  {
    key: PopularSectionTabsEnum.All,
    title: 'Tất cả',
  },
  {
    key: PopularSectionTabsEnum.BigWin,
    title: 'Thắng lớn',
  },
  {
    key: PopularSectionTabsEnum.LuckyGame,
    title: 'Game may mắn',
  },
]

const onSelectTab = (tab: PopularSectionTabsEnum): void => {
  params.groups = tab
  params.limit = POPULAR_SECTION_GAMES_LIMIT
  refresh()
}

const skeletonGames = computed(() => {
  return Array.from({ length: 6 }, () => SKELETON_POPULAR_GAME)
})

const categoryDropdowns = computed(() => {
  return tabItems.map((menu) => {
    return {
      key: menu.key,
      title: menu.title,
    }
  })
})

onMounted(() => {
  tab.value = PopularSectionTabsEnum.All
})
</script>

<template>
  <div v-if="lobbyGames?.length" class="popular-section">
    <BaseSection title="Phổ biến" icon="icon-trending">
      <div class="popular-section__content">
        <BaseSwiper
          v-if="!isFirstLoad"
          :items="status === 'success' ? lobbyGames : skeletonGames"
          :options="swiperOptions"
          :item-slides-per-view="SECTION_GAMES_LIMIT"
        >
          <template #item="{ item }: { item: GameItem }">
            <CommonGameItem :item="item" />
          </template>
        </BaseSwiper>
        <div v-else class="popular-section__placeholder-games">
          <CommonGameItem
            v-for="item in lobbyGames.slice(0, 6)"
            :key="item.ID"
            :item="item"
          />
        </div>
      </div>
      <template #right-action>
        <template v-if="!$isLighthouseBot">
          <BaseTabs v-if="!isMobile" v-model="tab" :tabs="tabItems" />
          <FormDropdown
            v-else
            :options="categoryDropdowns"
            :clearable="false"
            placeholder="Tất cả"
            name="filterName"
            scroll-behavior="close"
            offset="4"
            @on-model-change="tab = $event"
          >
            <template #item="{ item, props: itemProps }">
              <div
                :class="[
                  'item-dropdown',
                  { 'item-dropdown--active': tab === item.value },
                ]"
                v-bind="itemProps"
                title=""
              >
                {{ item.title }}
              </div>
            </template>
            <template #selection="{ item }">
              <div class="item-dropdown-selection-item">
                {{ item.title }}
              </div>
            </template>
          </FormDropdown>
        </template>
      </template>
    </BaseSection>
    <DesktopHomeTopDailyWinner />
  </div>
</template>

<style scoped lang="scss">
@import '~/assets/styles/components/desktop/home/popular-section.scss';
@import '~/assets/styles/components/responsive/desktop/home/popular-section.scss';
</style>
